import React, { useEffect } from 'react'
import { ButtonGroup, Card, Col, Row } from 'react-bootstrap'
import type { InvoiceView } from '@ztechapps/common.contract'
import { buildDocumentLinkPdfUrl, getWindow } from '@ztechapps/common.utils'
import { useDocument, useHistory } from '@ztechapps/common.hook'
import { LocalDate } from '@ztechapps/webui.date'
import { BusyButton } from '@ztechapps/webui.button'
import { OrderPaymentStatusBadge } from '@ztechapps/order.webui'
import { shouldShowCheckout } from '@ztechapps/order.common'
import { HorizontalStack, LoadingContent, ShowIf } from '@ztechapps/webui.util'
import { routesDef } from '../../config/routes'
import { PageTitle } from '../../components/PageTitle'
import { OrderItemsLarge } from '../../components/Order/OrderItemsLarge'
import { OrderItemsSmall } from '../../components/Order/OrderItemsSmall'
import { OrderSummary } from '../../components/Order/OrderSummary'
import { useGotoCart } from '../../hooks/useGotoCart'
import { getInvoiceByDocumentLinkQuery } from './graphql'
import { hasLocalSession } from './hasLocalSession'

const Address = ({ label, fullAddress }: { label: string; fullAddress?: string }) => (
  <div className="mb-3">
    <strong className="fw-bold d-block">{label}</strong>
    {fullAddress}
  </div>
)

/** The invoice a customer opens from an emailed link: the owner's invoice page without Cancel or Pay, with Checkout shown only while the order still needs payment. */
export const ReadOnlyInvoice = ({ token }: { token?: string }) => {
  const history = useHistory()
  const { document, error } = useDocument<InvoiceView>({ query: getInvoiceByDocumentLinkQuery, filter: { token } })
  // This page's Apollo client deliberately carries no access token (PII-driven anonymity), so
  // useGotoCart's own `useUser()` check is always false here, even for a visitor signed in on
  // this device — it would always show the "login first" dialog. hasLocalSession() reads that
  // directly from local storage so a signed-in visitor lands on Cart with no sign-in step, while
  // an anonymous visitor still goes through useGotoCart's existing sign-in-then-redirect flow.
  const [goToCart] = useGotoCart(document?._id)

  const onCheckout = () =>
    hasLocalSession() ? history.push(routesDef.CartPage.altPath({ orderId: document?._id ?? '' })) : goToCart()

  // Unknown, expired, revoked and wrong-type tokens all fail the query and end on the same page.
  useEffect(() => {
    if (error) history.push(routesDef.InvoiceViewExpiredPage.altPath(), { replace: true })
  }, [error, history])

  if (!document) return <LoadingContent />

  const { code, status, createdAt, paymentStatus, payments, billingAddress, shippingAddress, items } = document
  const { gross, shippingFee, taxAmount, discount, net } = document
  const pdfUrl = buildDocumentLinkPdfUrl({ token })
  const showCheckout = shouldShowCheckout(status, paymentStatus)

  return (
    <>
      <PageTitle title={`Invoice #${code}`} description={`View details of your ${status} order #${code}`} />
      <Card>
        <Card.Header>
          <Row className="g-3">
            <Col lg={6}>
              <h4>{status}</h4>
            </Col>
            <Col lg={6}>
              <ButtonGroup>
                <BusyButton
                  variant="primary"
                  icon="print"
                  text="Print"
                  onClick={() => getWindow().open(pdfUrl, '_blank', 'noopener,noreferrer')}
                />
                <ShowIf condition={showCheckout}>
                  <BusyButton icon="shopping-cart" text="Checkout" onClick={onCheckout} />
                </ShowIf>
              </ButtonGroup>
            </Col>
            <Col lg={6}>
              <HorizontalStack gap={2}>
                <div>
                  <strong className="fw-bold">Order#:</strong> {code}
                </div>
                <OrderPaymentStatusBadge paymentStatus={paymentStatus} payments={payments} />
              </HorizontalStack>
            </Col>
            <Col lg={6}>
              <strong className="fw-bold">Order Date:</strong> <LocalDate value={createdAt} />
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col lg={6}>
              <Address label="Bill To" fullAddress={billingAddress?.fullAddress} />
            </Col>
            <Col lg={6}>
              <Address label="Ship To" fullAddress={shippingAddress?.fullAddress} />
            </Col>
          </Row>
          <div className="py-3">
            <OrderItemsLarge items={items} />
            <OrderItemsSmall items={items} />
            <OrderSummary {...{ gross, shippingFee, taxAmount, discount, net }} />
          </div>
        </Card.Body>
      </Card>
    </>
  )
}
